/**
 * The Klaviyo API revision the subscription payload is written against. Pinned, never `latest`,
 * and kept in step with `back-in-stock-drawer.js`: a breaking change on Klaviyo's side must not
 * reach the form without the theme changing.
 */
const KLAVIYO_REVISION = '2026-07-15';

/**
 * Klaviyo's client-side subscription endpoint. It only accepts the account's public key (the
 * site id), which is what makes calling it from a theme safe.
 */
const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

/** Where the consent was collected, as Klaviyo records it on the profile. */
const KLAVIYO_SOURCE = 'Theme email signup';

/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 *
 * When the block is given a Klaviyo list, it also takes over the submit and sends the address
 * to that list instead of letting the form post to Shopify.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;
    /** @type {AbortController | undefined} */
    this.abortController = undefined;
    /** @type {boolean} */
    this.submitting = false;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.handleKlaviyoSubmit = this.handleKlaviyoSubmit.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');
    this.form = this.querySelector('form');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    // Both attributes are only rendered when the list and the public key are set.
    if (this.form && this.dataset.klaviyoList && this.dataset.klaviyoCompany) {
      this.form.addEventListener('submit', this.handleKlaviyoSubmit);
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }

    this.form?.removeEventListener('submit', this.handleKlaviyoSubmit);
    this.abortController?.abort();
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }

  /**
   * Sends the address to the Klaviyo list. Only reached once the browser's own checks have
   * passed: `required` and `type="email"` stop the submit event before it fires.
   *
   * @param {SubmitEvent} event - The form's submit event.
   */
  async handleKlaviyoSubmit(event) {
    event.preventDefault();

    if (this.submitting || !this.form || !this.input) return;

    // A bot filling every field it finds trips the honeypot. It gets the same confirmation a
    // visitor gets -- telling it what gave it away would only help it come back.
    const trap = this.querySelector('[data-klaviyo-trap]');
    if (trap instanceof HTMLInputElement && trap.value) {
      this.showKlaviyoMessage('success');
      return;
    }

    const email = /** @type {HTMLInputElement} */ (this.input).value.trim();

    this.submitting = true;
    this.form.setAttribute('aria-busy', 'true');
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.klaviyoCompany ?? '')}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION,
          },
          body: JSON.stringify(this.buildKlaviyoPayload(email)),
          signal: this.abortController.signal,
        }
      );

      // A 202 comes back with no body at all, so the status code is the only answer there is.
      if (response.ok) {
        this.form.reset();
        this.showKlaviyoMessage('success');
        return;
      }

      console.error(`Email signup failed: Klaviyo answered ${response.status}`);
      this.showKlaviyoMessage((await this.rejectsEmail(response)) ? 'invalid' : 'error');
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;

      // No answer at all: the visitor is offline, or a content blocker stopped a.klaviyo.com.
      console.error('Email signup failed', error);
      this.showKlaviyoMessage('error');
    } finally {
      this.submitting = false;
      this.form?.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} email - The address as typed, trimmed.
   * @returns {object} The JSON:API document Klaviyo expects.
   */
  buildKlaviyoPayload(email) {
    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: KLAVIYO_SOURCE,
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } },
                },
              },
            },
          },
        },
        relationships: {
          list: {
            data: { type: 'list', id: this.dataset.klaviyoList },
          },
        },
      },
    };
  }

  /**
   * Tells a rejected address apart from every other refusal. Only the former is the visitor's
   * to fix; a wrong list id or an outage on Klaviyo's side must not be blamed on what they typed.
   *
   * @param {Response} response - A non-2xx answer from Klaviyo.
   * @returns {Promise<boolean>} Whether Klaviyo pointed at the email field itself.
   */
  async rejectsEmail(response) {
    if (response.status !== 400) return false;

    try {
      /** @type {{ errors?: { source?: { pointer?: string } }[] }} */
      const body = await response.json();
      return body.errors?.some((error) => error.source?.pointer?.endsWith('/email')) ?? false;
    } catch {
      return false;
    }
  }

  /**
   * Reveals one of the answers the block rendered up front and hides the others.
   *
   * @param {'success' | 'invalid' | 'error'} type - Which answer to show.
   */
  showKlaviyoMessage(type) {
    for (const message of this.querySelectorAll('[data-klaviyo-message]')) {
      if (message instanceof HTMLElement) message.hidden = message.dataset.klaviyoMessage !== type;
    }

    if (type === 'invalid') {
      this.input?.setAttribute('aria-invalid', 'true');
    } else {
      this.input?.removeAttribute('aria-invalid');
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
